import AssessmentEventEditor from '../components/AssessmentEventEditor';
import React, { useMemo, useState } from 'react';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { assessmentsService } from '../api/assessmentManagementService';
import { staffingService } from '../api/staffingService';
import AssessorPicker from '../components/AssessorPicker';
const AssessmentManage: React.FC = () => {
    const { id } = useParams();
    const assessmentId = useMemo(() => parseInt(id || '0', 10), [id]);
    const queryClient = useQueryClient();
    const [candidateSearch, setCandidateSearch] = useState('');
    const [portalCandidateId, setPortalCandidateId] = useState<string>('');
    const [candidateTeamKey, setCandidateTeamKey] = useState<'A' | 'B'>('A');
    const [activeTeamKey, setActiveTeamKey] = useState<'A' | 'B'>('A');
    const [activeTab, setActiveTab] = useState<'candidates' | 'day-schedule' | 'schedule' | 'assignments' | 'reports'>('candidates');
    const [expandedReportIds, setExpandedReportIds] = useState<Set<number>>(new Set());
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [newSlotDraft, setNewSlotDraft] = useState({
        dayIndex: 0,
        startTime: '10:30',
        endTime: '11:20',
        label: '',
        slotType: 'break' as 'session' | 'break' | 'meal' | 'other',
        location: '',
        isInterviewPeriod: false,
        interviewPeriodIndex: 1,
    });
    const assessmentQuery = useQuery({
        queryKey: ['admin', 'assessments', assessmentId],
        queryFn: () => assessmentsService.get(assessmentId),
        enabled: assessmentId > 0,
    });
    const roleDefinitionsQuery = useQuery({
        queryKey: ['admin', 'assessmentRoles'],
        queryFn: () => assessmentsService.listRoleDefinitions(),
    });
    const eventRolesQuery = useQuery({
        queryKey: ['admin', 'assessments', assessmentId, 'eventRoles'],
        queryFn: () => assessmentsService.listEventRoles(assessmentId),
        enabled: assessmentId > 0,
    });
    const templatesQuery = useQuery({
        queryKey: ['admin', 'assessments', 'scheduleTemplates'],
        queryFn: () => assessmentsService.listTemplates(),
    });
    const masterScheduleQuery = useQuery({
        queryKey: ['admin', 'assessments', assessmentId, 'master-schedule'],
        queryFn: () => assessmentsService.getMasterSchedule(assessmentId),
        enabled: assessmentId > 0 && (activeTab === 'day-schedule' || activeTab === 'schedule'),
    });
    const invalidateMasterSchedule = async () => {
        await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'master-schedule'] });
    };
    const applyTemplateMutation = useMutation({
        mutationFn: async (templateId: number) => assessmentsService.applyTemplate(assessmentId, templateId, true),
        onSuccess: async () => {
            await invalidateMasterSchedule();
        },
    });
    const createSlotMutation = useMutation({
        mutationFn: async () => assessmentsService.createSlot(assessmentId, {
            dayIndex: newSlotDraft.dayIndex,
            startTime: newSlotDraft.startTime,
            endTime: newSlotDraft.endTime,
            label: newSlotDraft.label || null,
            slotType: newSlotDraft.isInterviewPeriod ? 'session' : newSlotDraft.slotType,
            location: newSlotDraft.location || null,
            isInterviewPeriod: newSlotDraft.isInterviewPeriod,
            interviewPeriodIndex: newSlotDraft.isInterviewPeriod ? newSlotDraft.interviewPeriodIndex : null,
            visibility: newSlotDraft.isInterviewPeriod ? 'assigned' : 'all',
        }),
        onSuccess: async () => {
            await invalidateMasterSchedule();
            setNewSlotDraft((prev) => ({ ...prev, label: '', location: '' }));
        },
    });
    const updateSlotMutation = useMutation({
        mutationFn: async (vars: {
            slotId: number;
            startTime?: string;
            endTime?: string;
            label?: string | null;
            location?: string | null;
            slotType?: 'session' | 'break' | 'meal' | 'other';
            dayIndex?: number;
        }) => assessmentsService.updateSlot(assessmentId, vars.slotId, vars),
        onSuccess: async () => {
            await invalidateMasterSchedule();
        },
    });
    const deleteSlotMutation = useMutation({
        mutationFn: async (slotId: number) => assessmentsService.deleteSlot(assessmentId, slotId),
        onSuccess: async () => {
            await invalidateMasterSchedule();
        },
    });
    const staffingQuery = useQuery({ queryKey: ['staffing', assessmentId], queryFn: () => staffingService.list(assessmentId) });
    const selectableUsers = staffingQuery.data?.data || [];
    const setEventRolesMutation = useMutation({
        mutationFn: async (roleIds: number[]) => assessmentsService.setEventRoles(assessmentId, roleIds),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'eventRoles'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId] });
        },
    });
    const assignEventRoleMutation = useMutation({
        mutationFn: async (vars: {
            roleId: number;
            assignedUserId?: number | null;
            assignedName?: string | null;
            assistantUserId?: number | null;
            assistantName?: string | null;
        }) => assessmentsService.assignEventRole(assessmentId, vars.roleId, {
            assignedUserId: vars.assignedUserId,
            assignedName: vars.assignedName,
            assistantUserId: vars.assistantUserId,
            assistantName: vars.assistantName,
        }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'eventRoles'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId] });
        },
    });
    const assignEventRoleTeamMutation = useMutation({
        mutationFn: async (vars: {
            roleId: number;
            teamKey: 'A' | 'B';
            assignedUserId?: number | null;
            assignedName?: string | null;
            assistantUserId?: number | null;
            assistantName?: string | null;
        }) => assessmentsService.assignEventRoleTeam(assessmentId, vars.roleId, vars.teamKey, {
            assignedUserId: vars.assignedUserId,
            assignedName: vars.assignedName,
            assistantUserId: vars.assistantUserId,
            assistantName: vars.assistantName,
        }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'eventRoles'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId] });
        },
    });
    const updateTeamModeMutation = useMutation({
        mutationFn: async (isDoubleAssessment: boolean) => assessmentsService.updateTeamMode(assessmentId, isDoubleAssessment),
        onSuccess: async () => {
            setActiveTeamKey('A');
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'eventRoles'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'interview-matrix'] });
        },
    });
    const candidatesQuery = useQuery({
        queryKey: ['admin', 'portalCandidates', 'all'],
        queryFn: async () => {
            // small list in practice; still cap it
            return staffingService.candidates();
        },
    });
    const addParticipantMutation = useMutation({
        mutationFn: async () => {
            const cid = parseInt(portalCandidateId, 10);
            if (!cid)
                throw new Error('Portal candidate id is required');
            return assessmentsService.addPortalCandidate(assessmentId, cid, candidateTeamKey);
        },
        onSuccess: async () => {
            setPortalCandidateId('');
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'interview-matrix'] });
        },
    });
    const removeParticipantMutation = useMutation({
        mutationFn: async (participantId: number) => assessmentsService.removeParticipant(assessmentId, participantId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'interview-matrix'] });
        },
    });
    const updateParticipantTeamMutation = useMutation({
        mutationFn: async (vars: {
            participantId: number;
            teamKey: 'A' | 'B';
        }) => assessmentsService.updateParticipantTeam(assessmentId, vars.participantId, vars.teamKey),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'interview-matrix'] });
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'report-assignments'] });
        },
    });
    const interviewMatrixQuery = useQuery({
        queryKey: ['admin', 'assessments', assessmentId, 'interview-matrix'],
        queryFn: async () => assessmentsService.getInterviewMatrix(assessmentId),
        enabled: !!assessmentId,
    });
    const reportAssignmentsQuery = useQuery({
        queryKey: ['admin', 'assessments', assessmentId, 'report-assignments'],
        queryFn: () => assessmentsService.listReportAssignments(assessmentId),
        enabled: assessmentId > 0,
    });
    const autoPopulateMatrixMutation = useMutation({
        mutationFn: async () => assessmentsService.autoPopulateInterviewMatrix(assessmentId, true, activeTeamKey),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'interview-matrix'] });
        },
    });
    const setMatrixCellMutation = useMutation({
        mutationFn: async (payload: {
            interviewPeriodIndex: number;
            roleId: number;
            participantId: number | null;
            teamKey?: 'A' | 'B';
        }) => assessmentsService.setInterviewMatrixCell(assessmentId, payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', assessmentId, 'interview-matrix'] });
        },
    });
    const sendReminderMutation = useMutation({
        mutationFn: async () => assessmentsService.sendAttendanceReminder(assessmentId),
    });
    const participants = assessmentQuery.data?.data?.participants || [];
    const roleDefinitions = roleDefinitionsQuery.data?.data || [];
    const eventRoles = useMemo(() => eventRolesQuery.data?.data || [], [eventRolesQuery.data]);
    const selectedRoleIds = new Set(eventRoles.map((er: any) => er.roleId));
    const eventRoleByRoleId = useMemo(() => {
        const m = new Map<number, any>();
        eventRoles.forEach((er: any) => m.set(er.roleId, er));
        return m;
    }, [eventRoles]);
    const existingPortalCandidateIds = new Set(participants
        .map((p) => p.portalCandidate?.id)
        .filter((v): v is number => typeof v === 'number'));
    const allCandidates = (candidatesQuery.data?.data || []) as Array<{
        id: number;
        name: string;
    }>;
    const candidateOptions = allCandidates
        .filter(c => (c.name || "").toLowerCase().includes(candidateSearch.toLowerCase()))
        .filter((c) => !existingPortalCandidateIds.has(c.id))
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return (<Layout>
      <div className="space-y-6">
        {assessmentQuery.isLoading && <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">Loading…</div>}
        {assessmentQuery.error && <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">Failed to load assessment.</div>}

        {assessmentQuery.data?.data && (<>
            <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">{assessmentQuery.data.data.name}</h1>
                  {assessmentQuery.data.data.isDoubleAssessment && (<span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">Double assessment</span>)}
                </div>
                <p className="text-gray-600 mt-2">
                  {new Date(assessmentQuery.data.data.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  {assessmentQuery.data.data.location ? ` • ${assessmentQuery.data.data.location}` : ''}
                </p>
              </div>
              <label className="inline-flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800">
                <input type="checkbox" checked={Boolean(assessmentQuery.data.data.isDoubleAssessment)} disabled={updateTeamModeMutation.isPending} onChange={(e) => {
                const label = e.target.checked ? 'enable double assessment mode' : 'return this event to a single assessment';
                if (window.confirm(`Are you sure you want to ${label}? This is blocked after reporting begins.`)) {
                    updateTeamModeMutation.mutate(e.target.checked);
                }
            }}/>
                Double assessment
              </label>
            </div>
            {updateTeamModeMutation.isError && (<div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {(updateTeamModeMutation.error as any)?.response?.data?.message || 'Unable to change assessment mode.'}
              </div>)}

            <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 space-y-4">
              <div>
                <Link className="underline text-primary-700" to={`/assessments/${assessmentId}`}>View assessment and final reports</Link><h2 className="text-lg font-semibold text-gray-900">Attendance Reminder</h2><Link className="underline text-primary-700" to={`/assessments/${assessmentId}/event-details`}>Manage attendance and hotel details</Link>
                <p className="text-sm text-gray-600">
                  Send the attendance reminder to assigned users.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:opacity-50 text-sm" disabled={sendReminderMutation.isPending} onClick={() => sendReminderMutation.mutate()}>
                  Send reminder
                </button>
                {sendReminderMutation.isSuccess && (<span className="text-sm text-green-700">
                    Reminder sent to {sendReminderMutation.data?.notified || 0} users.
                  </span>)}
                {sendReminderMutation.isError && (<span className="text-sm text-red-700">Failed to send reminder.</span>)}
              </div>
            </div>

            <AssessmentEventEditor key={assessmentId} event={assessmentQuery.data.data}/>
            {/* Tabs */}
            <div className="bg-white shadow-sm rounded-xl">
              <div className="flex gap-6 px-4 sm:px-6 border-b border-gray-200 overflow-x-auto">
                {[
                { key: 'candidates', label: 'Candidates' },
                { key: 'day-schedule', label: 'Day Schedule' },
                { key: 'schedule', label: 'Interview Matrix' },
                { key: 'assignments', label: 'Assignments' },
                { key: 'reports', label: 'Reports' },
            ].map((t) => {
                const isActive = activeTab === (t.key as any);
                return (<button key={t.key} type="button" className={[
                        'py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px',
                        isActive ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-900',
                    ].join(' ')} onClick={() => setActiveTab(t.key as any)}>
                      {t.label}
                    </button>);
            })}
              </div>
            </div>

            {activeTab === 'candidates' && (<div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Candidates</h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input aria-label="Search candidates" className="border rounded px-3 py-2" placeholder="Search candidates" value={candidateSearch} onChange={e => setCandidateSearch(e.target.value)}/>
                  <select className="w-full sm:w-96 border border-gray-300 rounded-md px-3 py-2" value={portalCandidateId} onChange={(e) => setPortalCandidateId(e.target.value)} disabled={candidatesQuery.isLoading}>
                    <option value="">
                      {candidatesQuery.isLoading ? 'Loading candidates…' : 'Select a portal candidate…'}
                    </option>
                    {candidateOptions.map((c) => (<option key={c.id} value={c.id}>
                        {c.name}
                      </option>))}
                  </select>
                  {assessmentQuery.data.data.isDoubleAssessment && (<select className="border border-gray-300 rounded-md px-3 py-2 font-medium" value={candidateTeamKey} onChange={(e) => setCandidateTeamKey(e.target.value as 'A' | 'B')}>
                      <option value="A">Team A</option>
                      <option value="B">Team B</option>
                    </select>)}
                  <button className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50" disabled={addParticipantMutation.isPending || !portalCandidateId} onClick={() => addParticipantMutation.mutate()}>
                    Add Candidate
                  </button>
                </div>
                {addParticipantMutation.isError && (<div className="text-sm text-red-700">{(addParticipantMutation.error as Error).message}</div>)}
                {candidatesQuery.isError && (<div className="text-sm text-red-700">
                    Failed to load candidate list. You can still add by ID using the portal candidates page.
                  </div>)}

                <ResponsiveTableWrapper>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        {assessmentQuery.data.data.isDoubleAssessment && (<th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Team</th>)}
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {participants.map((p) => (<tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900"><Link className="underline text-primary-700" to={`/assessments/${assessmentId}/candidates/${p.portalCandidate?.id}`}>{p.portalCandidate?.name || '—'}</Link></td>
                          <td className="px-4 py-3 text-sm text-gray-700">{p.status}</td>
                          {assessmentQuery.data.data.isDoubleAssessment && (<td className="px-4 py-3">
                              <select className="rounded-md border border-gray-300 px-2 py-1 text-sm font-semibold" value={p.teamKey || 'A'} disabled={updateParticipantTeamMutation.isPending} onChange={(e) => updateParticipantTeamMutation.mutate({
                            participantId: p.id,
                            teamKey: e.target.value as 'A' | 'B',
                        })}>
                                <option value="A">Team A</option>
                                <option value="B">Team B</option>
                              </select>
                            </td>)}
                          <td className="px-4 py-3 text-right">
                            <button className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50" disabled={removeParticipantMutation.isPending} onClick={() => { if (window.confirm('Remove this candidate from the assessment?'))
                    removeParticipantMutation.mutate(p.id); }}>
                              Remove
                            </button>
                          </td>
                        </tr>))}
                      {participants.length === 0 && (<tr>
                          <td className="px-4 py-6 text-sm text-gray-600" colSpan={assessmentQuery.data.data.isDoubleAssessment ? 4 : 3}>
                            No candidates yet.
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </ResponsiveTableWrapper>
                {updateParticipantTeamMutation.isError && (<div className="text-sm text-red-700">
                    {(updateParticipantTeamMutation.error as any)?.response?.data?.message || 'Unable to move candidate.'}
                  </div>)}
              </div>)}

            {activeTab === 'day-schedule' && (<div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Day Schedule</h2>
                  <p className="text-sm text-gray-600">
                    Whole-day agenda for the assessment. Interview periods appear as “Interview Slot #N” (details live in the Interview Matrix).
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apply schedule template</label>
                    <select className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
                      <option value="">Select a template…</option>
                      {(templatesQuery.data?.data || []).map((t) => (<option key={t.id} value={t.id}>
                          {t.isDefault ? 'Default • ' : ''}{t.name}
                        </option>))}
                    </select>
                  </div>
                  <button type="button" className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 text-sm" disabled={!selectedTemplateId || applyTemplateMutation.isPending} onClick={() => {
                    const tid = parseInt(selectedTemplateId, 10);
                    if (tid && window.confirm('Apply this template and replace the current schedule?'))
                        applyTemplateMutation.mutate(tid);
                }}>
                    Apply template (replaces current)
                  </button>
                </div>
                {applyTemplateMutation.isError && (<div className="text-sm text-red-700">
                    {(applyTemplateMutation.error as any)?.response?.data?.message || 'Failed to apply template.'}
                  </div>)}

                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">Add row</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <label className="text-xs text-gray-600">
                      Day index
                      <input type="number" min={0} className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={newSlotDraft.dayIndex} onChange={(e) => setNewSlotDraft((prev) => ({ ...prev, dayIndex: parseInt(e.target.value, 10) || 0 }))}/>
                    </label>
                    <label className="text-xs text-gray-600">
                      Start
                      <input type="time" className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={newSlotDraft.startTime} onChange={(e) => setNewSlotDraft((prev) => ({ ...prev, startTime: e.target.value }))}/>
                    </label>
                    <label className="text-xs text-gray-600">
                      End
                      <input type="time" className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={newSlotDraft.endTime} onChange={(e) => setNewSlotDraft((prev) => ({ ...prev, endTime: e.target.value }))}/>
                    </label>
                    <label className="text-xs text-gray-600">
                      Label
                      <input type="text" className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={newSlotDraft.label} placeholder={newSlotDraft.isInterviewPeriod ? 'Optional' : 'e.g. Lunch'} onChange={(e) => setNewSlotDraft((prev) => ({ ...prev, label: e.target.value }))}/>
                    </label>
                    <label className="text-xs text-gray-600">
                      Location
                      <input type="text" className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={newSlotDraft.location} onChange={(e) => setNewSlotDraft((prev) => ({ ...prev, location: e.target.value }))}/>
                    </label>
                    <label className="text-xs text-gray-600">
                      Type
                      <select className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={newSlotDraft.isInterviewPeriod ? 'interview' : newSlotDraft.slotType} onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'interview') {
                        setNewSlotDraft((prev) => ({ ...prev, isInterviewPeriod: true, slotType: 'session' }));
                    }
                    else {
                        setNewSlotDraft((prev) => ({
                            ...prev,
                            isInterviewPeriod: false,
                            slotType: value as 'session' | 'break' | 'meal' | 'other',
                        }));
                    }
                }}>
                        <option value="interview">Interview Slot</option>
                        <option value="break">Break</option>
                        <option value="meal">Meal</option>
                        <option value="session">Session</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                  </div>
                  {newSlotDraft.isInterviewPeriod && (<label className="text-xs text-gray-600 inline-flex items-center gap-2">
                      Interview slot #
                      <input type="number" min={1} max={12} className="w-20 border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={newSlotDraft.interviewPeriodIndex} onChange={(e) => setNewSlotDraft((prev) => ({
                        ...prev,
                        interviewPeriodIndex: parseInt(e.target.value, 10) || 1,
                    }))}/>
                    </label>)}
                  <button type="button" className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 text-sm" disabled={createSlotMutation.isPending || !newSlotDraft.startTime || !newSlotDraft.endTime} onClick={() => createSlotMutation.mutate()}>
                    Add to schedule
                  </button>
                  {createSlotMutation.isError && (<div className="text-sm text-red-700">
                      {(createSlotMutation.error as any)?.response?.data?.message || 'Failed to add slot.'}
                    </div>)}
                </div>

                {masterScheduleQuery.isLoading ? (<div className="text-sm text-gray-600">Loading day schedule…</div>) : masterScheduleQuery.isError ? (<div className="text-sm text-red-700">Failed to load day schedule.</div>) : !(masterScheduleQuery.data?.data?.days || []).length ? (<div className="text-sm text-gray-600">No day schedule published yet. Apply a template or add rows above.</div>) : (<div className="space-y-6">
                    {(masterScheduleQuery.data?.data?.days || []).map((day) => (<div key={day.dayIndex} className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Day {day.dayIndex + 1}
                          {day.date ? <span className="ml-2 font-normal text-gray-500">{day.date}</span> : null}
                        </h3>
                        <ResponsiveTableWrapper>
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Start</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">End</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Label</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Location</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {day.items.map((item) => {
                            const isInterview = item.slotType === 'interview';
                            return (<tr key={`${day.dayIndex}-${item.representativeSlotId}`}>
                                    <td className="px-3 py-2">
                                      <input type="time" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" defaultValue={String(item.startTime).slice(0, 5)} onBlur={(e) => {
                                    if (e.target.value && e.target.value !== String(item.startTime).slice(0, 5)) {
                                        updateSlotMutation.mutate({
                                            slotId: item.representativeSlotId,
                                            startTime: e.target.value,
                                        });
                                    }
                                }}/>
                                    </td>
                                    <td className="px-3 py-2">
                                      <input type="time" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" defaultValue={String(item.endTime).slice(0, 5)} onBlur={(e) => {
                                    if (e.target.value && e.target.value !== String(item.endTime).slice(0, 5)) {
                                        updateSlotMutation.mutate({
                                            slotId: item.representativeSlotId,
                                            endTime: e.target.value,
                                        });
                                    }
                                }}/>
                                    </td>
                                    <td className="px-3 py-2">
                                      {isInterview ? (<span className="text-sm text-gray-900">{item.label}</span>) : (<input type="text" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" defaultValue={item.label || ''} onBlur={(e) => {
                                        if (e.target.value !== (item.label || '')) {
                                            updateSlotMutation.mutate({
                                                slotId: item.representativeSlotId,
                                                label: e.target.value || null,
                                            });
                                        }
                                    }}/>)}
                                    </td>
                                    <td className="px-3 py-2">
                                      {isInterview ? (<span className="inline-flex rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                                          interview
                                        </span>) : (<select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" defaultValue={item.slotType} onChange={(e) => updateSlotMutation.mutate({
                                        slotId: item.representativeSlotId,
                                        slotType: e.target.value as 'session' | 'break' | 'meal' | 'other',
                                    })}>
                                          <option value="break">break</option>
                                          <option value="meal">meal</option>
                                          <option value="session">session</option>
                                          <option value="other">other</option>
                                        </select>)}
                                    </td>
                                    <td className="px-3 py-2">
                                      <input type="text" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" defaultValue={item.location || ''} onBlur={(e) => {
                                    if (e.target.value !== (item.location || '')) {
                                        updateSlotMutation.mutate({
                                            slotId: item.representativeSlotId,
                                            location: e.target.value || null,
                                        });
                                    }
                                }}/>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <button type="button" className="text-sm text-red-700 hover:underline" disabled={deleteSlotMutation.isPending} onClick={() => {
                                    if (window.confirm(`Delete “${item.label}”?`)) {
                                        deleteSlotMutation.mutate(item.representativeSlotId);
                                    }
                                }}>
                                        Delete
                                      </button>
                                    </td>
                                  </tr>);
                        })}
                            </tbody>
                          </table>
                        </ResponsiveTableWrapper>
                      </div>))}
                  </div>)}
              </div>)}

            {activeTab === 'schedule' && (<div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Interview Matrix</h2>
                    <p className="text-sm text-gray-600">
                      Assign each candidate to an interview area for each period.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {assessmentQuery.data.data.isDoubleAssessment && (['A', 'B'] as const).map((teamKey) => (<button key={teamKey} type="button" className={`rounded-md px-3 py-2 text-sm font-semibold ${activeTeamKey === teamKey ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveTeamKey(teamKey)}>
                        Team {teamKey}
                      </button>))}
                    <button className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50" disabled={autoPopulateMatrixMutation.isPending} onClick={() => autoPopulateMatrixMutation.mutate()}>
                      Auto-populate{assessmentQuery.data.data.isDoubleAssessment ? ` Team ${activeTeamKey}` : ''}
                    </button>
                  </div>
                </div>

                {autoPopulateMatrixMutation.isError && (<div className="text-sm text-red-700">
                    {(autoPopulateMatrixMutation.error as any)?.message || 'Failed to auto-populate'}
                  </div>)}

                {(() => {
                    const matrix = interviewMatrixQuery.data?.data;
                    const roles = matrix?.roles || [];
                    const periods = matrix?.periods || [1, 2, 3, 4, 5, 6];
                    const matrixParticipants = (matrix?.participants || participants || []).filter((p: any) => (p.teamKey || 'A') === activeTeamKey);
                    const cells = (matrix?.cells || []).filter((c: any) => (c.teamKey || 'A') === activeTeamKey);
                    const cellByPeriodRoleId = new Map<string, any>();
                    for (const c of cells as any[]) {
                        cellByPeriodRoleId.set(`${activeTeamKey}:${c.interviewPeriodIndex}:${c.roleId}`, c);
                    }
                    const candidateNameByParticipantId = new Map<number, string>();
                    for (const p of matrixParticipants as any[]) {
                        candidateNameByParticipantId.set(p.id, p.portalCandidate?.name || `Participant ${p.id}`);
                    }
                    if (interviewMatrixQuery.isLoading) {
                        return <div className="text-sm text-gray-700">Loading matrix…</div>;
                    }
                    if (roles.length === 0) {
                        return (<div className="text-sm text-gray-700">
                        No assessor interview-area roles selected. Enable roles in the Assignments tab (set them as Interview Area) to build the matrix.
                      </div>);
                    }
                    return (<ResponsiveTableWrapper>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                              Interview
                            </th>
                            {roles.map((r: any) => (<th key={r.id} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                <div className="flex flex-col">
                                  <span>{r.displayName || r.name || `Role ${r.id}`}</span>
                                  <span className="text-[11px] font-normal text-gray-500 normal-case">
                                    {r.interviewAudience === 'candidate_only'
                                ? 'Candidate only'
                                : r.interviewAudience === 'spouse_only'
                                    ? 'Spouse only'
                                    : 'Couple'}
                                  </span>
                                </div>
                              </th>))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {periods.map((pIdx: number) => (<tr key={pIdx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                                Interview {pIdx}
                              </td>
                              {roles.map((r: any) => {
                                const cell = cellByPeriodRoleId.get(`${activeTeamKey}:${pIdx}:${r.id}`);
                                const participantId = cell?.participantId;
                                const label = participantId ? candidateNameByParticipantId.get(participantId) : '';
                                return (<td key={r.id} className="px-3 py-2 text-sm text-gray-900">
                                    <div className="flex items-center gap-2">
                                      <select className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" value={participantId || ''} onChange={(e) => {
                                        const pid = e.target.value ? parseInt(e.target.value, 10) : null;
                                        setMatrixCellMutation.mutate({ interviewPeriodIndex: pIdx, roleId: r.id, participantId: pid, teamKey: activeTeamKey });
                                    }}>
                                        <option value="">{label ? label : '—'}</option>
                                        {(matrixParticipants as any[]).map((p) => (<option key={p.id} value={p.id}>
                                            {p.portalCandidate?.name || `Participant ${p.id}`}
                                          </option>))}
                                      </select>
                                      <button type="button" className="px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" onClick={() => setMatrixCellMutation.mutate({ interviewPeriodIndex: pIdx, roleId: r.id, participantId: null, teamKey: activeTeamKey })}>
                                        Clear
                                      </button>
                                    </div>
                                  </td>);
                            })}
                            </tr>))}
                        </tbody>
                      </table>
                    </ResponsiveTableWrapper>);
                })()}
              </div>)}

            {activeTab === 'assignments' && (<div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Assignments</h2>
                    {(staffingQuery.isError || assignEventRoleMutation.isError || assignEventRoleTeamMutation.isError || setEventRolesMutation.isError) && <p role="alert" className="text-red-700">{((assignEventRoleMutation.error || assignEventRoleTeamMutation.error || setEventRolesMutation.error) as any)?.response?.data?.message || 'Unable to load or save staffing. Please retry.'}</p>}
                    {(assignEventRoleMutation.isSuccess || assignEventRoleTeamMutation.isSuccess) && <p role="status" className="text-green-700">Assignment saved</p>}
                    <Link to="/assessments/assessors" className="underline text-primary-700">Manage assessor qualifications</Link>
                    <p className="text-sm text-gray-600">
                      Select which roles apply for this assessment, then choose a qualified primary assessor and an optional assistant.
                    </p>
                  </div>
                </div>

                <ResponsiveTableWrapper>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Use</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Primary assessor or staff</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Assistant</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Guest name (support roles)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {roleDefinitions.map((r: any) => {
                    const checked = selectedRoleIds.has(r.id);
                    const er = eventRoleByRoleId.get(r.id);
                    return (<tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <input type="checkbox" checked={checked} onChange={(e) => {
                            const next = new Set(Array.from(selectedRoleIds));
                            if (e.target.checked)
                                next.add(r.id);
                            else
                                next.delete(r.id);
                            if (e.target.checked || window.confirm('Remove this role and its staffing from the event?'))
                                setEventRolesMutation.mutate(Array.from(next));
                        }}/>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{r.displayName}</td>
                            <td className="px-4 py-3">
                              {assessmentQuery.data.data.isDoubleAssessment && r.isInterviewArea ? (<div className="space-y-2 min-w-56">
                                  {(['A', 'B'] as const).map((teamKey) => {
                                const assignment = er?.teamAssignments?.find((item: any) => item.teamKey === teamKey);
                                return (<label key={teamKey} className="flex items-center gap-2">
                                        <span className="w-14 text-xs font-semibold text-gray-600">Team {teamKey}</span>
                                        <AssessorPicker users={selectableUsers} role={r} assistant={false} value={checked ? (assignment?.assignedUserId || '') : ''} currentName={assignment?.assignedName || (assignment?.assignedUser ? `${assignment.assignedUser.firstName} ${assignment.assignedUser.lastName}` : '')} disabled={!checked || staffingQuery.isLoading || staffingQuery.isError} onChange={id => assignEventRoleTeamMutation.mutate({ roleId: r.id, teamKey, assignedUserId: id, assignedName: null })}/>
                                      </label>);
                            })}
                                </div>) : (<AssessorPicker users={selectableUsers} role={r} assistant={false} value={checked ? (er?.assignedUserId || '') : ''} currentName={er?.assignedName || (er?.assignedUser ? `${er.assignedUser.firstName} ${er.assignedUser.lastName}` : '')} disabled={!checked || staffingQuery.isLoading || staffingQuery.isError} onChange={id => assignEventRoleMutation.mutate({ roleId: r.id, assignedUserId: id, assignedName: null })}/>)}
                            </td>
                            <td className="px-4 py-3">
                              {assessmentQuery.data.data.isDoubleAssessment && r.isInterviewArea ? (<div className="space-y-2 min-w-56">
                                  {(['A', 'B'] as const).map((teamKey) => {
                                const assignment = er?.teamAssignments?.find((item: any) => item.teamKey === teamKey);
                                return (<label key={`assistant-${teamKey}`} className="flex items-center gap-2">
                                        <span className="w-14 text-xs font-semibold text-gray-600">Team {teamKey}</span>
                                        <AssessorPicker users={selectableUsers} role={r} assistant={true} value={checked ? (assignment?.assistantUserId || '') : ''} currentName={assignment?.assistantName || (assignment?.assistantUser ? `${assignment.assistantUser.firstName} ${assignment.assistantUser.lastName}` : '')} disabled={!checked || staffingQuery.isLoading || staffingQuery.isError} onChange={id => assignEventRoleTeamMutation.mutate({ roleId: r.id, teamKey, assistantUserId: id, assistantName: null })}/>
                                      </label>);
                            })}
                                </div>) : (<AssessorPicker users={selectableUsers} role={r} assistant={true} value={checked ? (er?.assistantUserId || '') : ''} currentName={er?.assistantName || (er?.assistantUser ? `${er.assistantUser.firstName} ${er.assistantUser.lastName}` : '')} disabled={!checked || staffingQuery.isLoading || staffingQuery.isError} onChange={id => assignEventRoleMutation.mutate({ roleId: r.id, assistantUserId: id, assistantName: null })}/>)}
                            </td>
                            <td className="px-4 py-3">
                              {assessmentQuery.data.data.isDoubleAssessment && r.isInterviewArea ? (<div className="space-y-2 min-w-44">
                                  {(['A', 'B'] as const).map((teamKey) => {
                                const assignment = er?.teamAssignments?.find((item: any) => item.teamKey === teamKey);
                                return (<input key={`${teamKey}-${assignment?.id || 'new'}`} className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" defaultValue={assignment?.assignedName || ''} disabled={!checked || r.roleType === 'assessor'} placeholder={`Team ${teamKey} optional`} onBlur={(e) => assignEventRoleTeamMutation.mutate({
                                        roleId: r.id,
                                        teamKey,
                                        assignedUserId: assignment?.assignedUserId || null,
                                        assignedName: e.target.value || null,
                                        assistantUserId: assignment?.assistantUserId ?? null,
                                        assistantName: assignment?.assistantName || null,
                                    })}/>);
                            })}
                                </div>) : (<input className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm" defaultValue={er?.assignedName || ''} disabled={!checked || r.roleType === 'assessor'} placeholder="Optional" onBlur={(e) => assignEventRoleMutation.mutate({
                                roleId: r.id,
                                assignedUserId: er?.assignedUserId ?? null,
                                assignedName: e.target.value || null,
                                assistantUserId: er?.assistantUserId ?? null,
                                assistantName: er?.assistantName || null,
                            })}/>)}
                            </td>
                          </tr>);
                })}
                      {roleDefinitions.length === 0 && (<tr>
                          <td className="px-4 py-6 text-sm text-gray-600" colSpan={8}>
                            No global roles defined yet. Create them in “Assessment Roles”.
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </ResponsiveTableWrapper>
              </div>)}

            {activeTab === 'reports' && (<div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Assessment Reports</h2>
                  <p className="text-sm text-gray-600">
                    Review and delete reports by candidate and assessment role.
                  </p>
                </div>

                {reportAssignmentsQuery.isLoading && (<div className="text-sm text-gray-700">Loading reports…</div>)}

                {reportAssignmentsQuery.isError && (<div className="text-sm text-red-700">Failed to load reports.</div>)}

                {!reportAssignmentsQuery.isLoading && !reportAssignmentsQuery.isError && (() => {
                    const assignments = reportAssignmentsQuery.data?.data || [];
                    const grouped = new Map<number, {
                        participant: any;
                        assignments: any[];
                    }>();
                    for (const assignment of assignments) {
                        const participant = assignment.participant;
                        if (!participant)
                            continue;
                        const current = grouped.get(participant.id) || { participant, assignments: [] };
                        current.assignments.push(assignment);
                        grouped.set(participant.id, current);
                    }
                    const groups = Array.from(grouped.values()).map((group) => ({
                        ...group,
                        assignments: group.assignments
                            .slice()
                            .sort((a, b) => ((a.role?.sortOrder ?? 0) - (b.role?.sortOrder ?? 0)) || ((a.role?.displayName || '').localeCompare(b.role?.displayName || ''))),
                    }));
                    if (!groups.length) {
                        return <div className="text-sm text-gray-600">No report assignments available yet.</div>;
                    }
                    const toggleExpanded = (participantId: number) => {
                        setExpandedReportIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(participantId)) {
                                next.delete(participantId);
                            }
                            else {
                                next.add(participantId);
                            }
                            return next;
                        });
                    };
                    return (<div className="space-y-4">
                      {groups.map((group) => {
                            const portalCandidate = group.participant?.portalCandidate;
                            const candidateName = portalCandidate?.name || '—';
                            const spouseName = portalCandidate?.spouseName || '';
                            const candidateLabel = spouseName ? `${candidateName} & ${spouseName}` : candidateName;
                            const isExpanded = expandedReportIds.has(group.participant.id);
                            return (<div key={group.participant.id} className="rounded-xl border border-gray-200 overflow-hidden">
                            <button type="button" onClick={() => toggleExpanded(group.participant.id)} className="w-full text-left px-4 py-3 sm:px-6 flex items-center justify-between gap-3 hover:bg-gray-50">
                              <div>
                                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                  {candidateLabel}
                                  {assessmentQuery.data.data.isDoubleAssessment && (<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">Team {group.participant.teamKey || 'A'}</span>)}
                                </div>
                                <div className="text-xs text-gray-500">Participant #{group.participant.id}</div>
                              </div>
                              <div className="text-sm text-gray-600">
                                {isExpanded ? 'Hide reports' : 'View reports'}
                              </div>
                            </button>

                            {isExpanded && (<div className="px-4 pb-4 sm:px-6 space-y-3">
                                {group.assignments.map((assignment) => {
                                        const roleLabel = assignment.role?.displayName || assignment.role?.name || 'Area';
                                        const assessorName = assignment.assessor
                                            ? `${assignment.assessor.firstName || ''} ${assignment.assessor.lastName || ''}`.trim()
                                            : '';
                                        const report = assignment.latestReport;
                                        const reportNotes = report?.content?.trim() || '';
                                        const afterPartNotes = report?.afterPartNotes?.trim() || '';
                                        return (<div key={`${assignment.participantId}-${assignment.roleId}`} className="rounded-lg border border-gray-200 p-4 space-y-3">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="text-sm font-semibold text-gray-900">{roleLabel}</div>
                                        <div className="text-xs text-gray-500">
                                          {assessorName ? `Assessor: ${assessorName}` : 'Assessor: —'}
                                        </div>
                                      </div>
                                      {report ? (<>
                                          <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Report Notes</div>
                                            <div className="text-sm text-gray-800 whitespace-pre-wrap">{reportNotes || '—'}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">After-Part Notes</div>
                                            <div className="text-sm text-gray-800 whitespace-pre-wrap">{afterPartNotes || '—'}</div>
                                          </div>
                                          <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
                                            <div className="text-xs text-gray-500">
                                              Status: <span className="text-gray-800">{report.status}</span>
                                            </div>

                                          </div>
                                        </>) : (<div className="text-sm text-gray-500">No report submitted yet.</div>)}
                                    </div>);
                                    })}
                              </div>)}
                          </div>);
                        })}
                    </div>);
                })()}
              </div>)}
          </>)}
      </div>
    </Layout>);
};
export default AssessmentManage;
